import {
  BoundMultiSelectField,
  BoundTextAreaField,
  BoundTextField,
  Css,
  FormLines,
  Icon,
  Palette,
  ToggleChipGroup,
  Tooltip,
} from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { AssetUppyFile } from "src/components/uploader/UppyUploader";
import { LotType, ReadyPlanAssetType } from "src/generated/graphqlTypes";
import { useMarketFilter } from "src/hooks";
import { PotentialOperationDetailsFragment } from "src/queries/fragments.generated";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { PlanPackageDetailsForm } from "../utils";
import { CreatePlanPackageCode } from "./CreatePlanPackageCode";
import { PlanPackageAssetUploader } from "./PlanPackageAssetUploader";
import { PlanPackageDetailsCard } from "./PlanPackageDetailsCard";

export type PlanOverviewCardProps = {
  formState: ObjectState<PlanPackageDetailsForm>;
  lotTypes: LotType[] | undefined | null;
  assetUrls: {
    coverPhoto: string | null | undefined;
    floorPlan: string | null | undefined;
    groundBasementFloorPlan: string | null | undefined;
    firstFloorPlan: string | null | undefined;
    secondFloorPlan: string | null | undefined;
    aduFirstFloorPlan: string | null | undefined;
    aduSecondFloorPlan: string | null | undefined;
  };
  saveAsset: (file: AssetUppyFile | undefined, assetType: ReadyPlanAssetType) => void;
  canEdit: PotentialOperationDetailsFragment | undefined;
};

export function PlanOverviewCard(props: PlanOverviewCardProps) {
  const { formState, lotTypes, canEdit, assetUrls, saveAsset } = props;
  const { options: markets } = useMarketFilter();

  return (
    <PlanPackageDetailsCard title="Plan Overview" width={731}>
      <div css={Css.bt.bcGray200.pt3.pb1.bn.pt0.$}>
        <FormLines width="full" labelStyle="left" gap={3} labelLeftFieldWidth="65%">
          <BoundTextField
            field={formState.name}
            label="Plan Name"
            placeholder="i.e. The Everett..."
            required
            disabled={disableBasedOnPotentialOperation(canEdit)}
          />

          <ToggleChipGroup
            label="Product Types(s)"
            options={Object.values(LotType).map((lotType) => ({
              label: lotType,
              value: lotType,
              disabled: disableBasedOnPotentialOperation(canEdit),
            }))}
            values={lotTypes ?? []}
            onChange={(values) => {
              formState.lotTypes.set(values as LotType[]);
            }}
          />
          <BoundMultiSelectField
            label="Market(s)"
            field={formState.markets}
            labelStyle="left"
            options={markets}
            disabled={disableBasedOnPotentialOperation(canEdit)}
          />
          <BoundTextAreaField
            label="Sales Description"
            field={formState.description}
            labelStyle="left"
            placeholder="i.e. This large single family residence is ideal for those looking for a first-level primary suite and the perfect balance between an open layout and private spaces. ..."
            disabled={disableBasedOnPotentialOperation(canEdit)}
          />
          <CreatePlanPackageCode
            codeField={formState.code}
            typeField={formState.type}
            disabled={disableBasedOnPotentialOperation(canEdit)}
          />
          <div css={Css.df.$}>
            <div css={Css.w("35%").fs0.$}>Plan Cover Photo</div>
            <PlanPackageAssetUploader
              assetUrl={assetUrls.coverPhoto}
              saveAsset={(file) => saveAsset(file, ReadyPlanAssetType.CoverPhoto)}
              assetType={ReadyPlanAssetType.CoverPhoto}
              disabled={disableBasedOnPotentialOperation(canEdit)}
            />
          </div>
          <div css={Css.df.aic.$}>
            <div css={Css.df.gap1.aic.w("35%").fs0.$}>
              <div>Plan Floor Plan</div>
              <Tooltip placement="right" title="Combined view">
                <Icon icon="infoCircle" color={Palette.Gray900} inc={2} />
              </Tooltip>
            </div>
            <PlanPackageAssetUploader
              assetUrl={assetUrls.floorPlan}
              saveAsset={(file) => saveAsset(file, ReadyPlanAssetType.FloorPlan)}
              assetType={ReadyPlanAssetType.FloorPlan}
              disabled={disableBasedOnPotentialOperation(canEdit)}
            />
          </div>
          <div css={Css.df.aic.$}>
            <div css={Css.w("35%").fs0.$}>Ground/Basement Floor Plan</div>
            <PlanPackageAssetUploader
              assetUrl={assetUrls.groundBasementFloorPlan}
              saveAsset={(file) => saveAsset(file, ReadyPlanAssetType.GroundBasementFloorPlan)}
              assetType={ReadyPlanAssetType.GroundBasementFloorPlan}
              disabled={disableBasedOnPotentialOperation(canEdit)}
            />
          </div>
          <div css={Css.df.aic.$}>
            <div css={Css.w("35%").fs0.$}>First Floor Plan</div>
            <PlanPackageAssetUploader
              assetUrl={assetUrls.firstFloorPlan}
              saveAsset={(file) => saveAsset(file, ReadyPlanAssetType.FirstFloorPlan)}
              assetType={ReadyPlanAssetType.FirstFloorPlan}
              disabled={disableBasedOnPotentialOperation(canEdit)}
            />
          </div>
          <div css={Css.df.aic.$}>
            <div css={Css.w("35%").fs0.$}>Second Floor Plan</div>
            <PlanPackageAssetUploader
              assetUrl={assetUrls.secondFloorPlan}
              saveAsset={(file) => saveAsset(file, ReadyPlanAssetType.SecondFloorPlan)}
              assetType={ReadyPlanAssetType.SecondFloorPlan}
              disabled={disableBasedOnPotentialOperation(canEdit)}
            />
          </div>
          <div css={Css.df.aic.$}>
            <div css={Css.w("35%").fs0.$}>ADU First Floor Plan</div>
            <PlanPackageAssetUploader
              assetUrl={assetUrls.aduFirstFloorPlan}
              saveAsset={(file) => saveAsset(file, ReadyPlanAssetType.AduFirstFloorPlan)}
              assetType={ReadyPlanAssetType.AduFirstFloorPlan}
              disabled={disableBasedOnPotentialOperation(canEdit)}
            />
          </div>
          <div css={Css.df.aic.$}>
            <div css={Css.w("35%").fs0.$}>ADU Second Floor Plan</div>
            <PlanPackageAssetUploader
              assetUrl={assetUrls.aduSecondFloorPlan}
              saveAsset={(file) => saveAsset(file, ReadyPlanAssetType.AduSecondFloorPlan)}
              assetType={ReadyPlanAssetType.AduSecondFloorPlan}
              disabled={disableBasedOnPotentialOperation(canEdit)}
            />
          </div>
          <BoundTextField
            field={formState.insideMapsProjectId}
            label="InsideMaps Project ID"
            disabled={disableBasedOnPotentialOperation(canEdit)}
          />
          <div css={Css.df.$}>
            <div css={Css.w("35%").fs0.$}>InsideMaps Config</div>
            <BoundTextAreaField
              field={formState.insideMapsConfig}
              maxLines={7}
              fullWidth
              labelStyle="hidden"
              disabled={disableBasedOnPotentialOperation(canEdit)}
            />
          </div>
        </FormLines>
      </div>
    </PlanPackageDetailsCard>
  );
}
